import { FileText } from 'lucide-react';
import type { ComprobanteResumen, EstadoComprobante } from '../../types/comprobante';
import { estaObjetado, indicacionDeObjecion, textoTiempoRestante, vencimientoEnChile } from '../../utils/comprobantePlazo';

const TEXTO_EXENTO = {
  donacion: 'Donación — no requiere comprobante',
  pago_automatico: 'Pago automático — sin transferencia',
  traspaso: 'Traspaso sin comprobante en marcha blanca',
  envio: 'Envío sin comprobante en marcha blanca',
  apertura: 'Apertura histórica — no requiere comprobante',
} as const;

interface EstadoComprobanteBadgeProps {
  readonly estado: EstadoComprobante;
  readonly ahora: Date;
  /** "Reembolso" cuando es el segundo respaldo del movimiento. */
  readonly etiqueta?: string;
  readonly onVer: (comprobante: ComprobanteResumen) => void;
  /** Adjuntar, o reemplazar cuando el comprobante fue objetado: el botón cambia de texto, la carga es la misma. */
  readonly onAdjuntar?: () => void;
}

export const EstadoComprobanteBadge: React.FC<EstadoComprobanteBadgeProps> = ({ estado, ahora, etiqueta, onVer, onAdjuntar }) => {
  const prefijo = etiqueta ? `${etiqueta}: ` : '';
  const adjuntar = onAdjuntar && (
    <button type="button" onClick={onAdjuntar} className="min-h-11 px-2 text-xs font-semibold text-blue-600 hover:text-blue-800">
      {estaObjetado(estado) ? 'Reemplazar comprobante' : 'Adjuntar'}
    </button>
  );

  if (estado.tipo === 'adjunto') {
    const paginas = estado.comprobante.rutas.length;
    return (
      <button type="button" onClick={() => { onVer(estado.comprobante); }}
        aria-label={`${prefijo}ver comprobante`}
        className="inline-flex items-center gap-1 min-h-11 min-w-11 px-2 rounded-lg text-slate-600 hover:bg-slate-100">
        <FileText className="w-5 h-5" aria-hidden="true" />
        {etiqueta && <span className="text-xs">{etiqueta}</span>}
        {paginas > 1 && <span className="text-xs">{paginas} págs.</span>}
      </button>
    );
  }

  if (estado.tipo === 'pendiente' || estado.tipo === 'vencido') {
    const vencido = estado.tipo === 'vencido';
    const texto = vencido ? 'Vencido' : `Pendiente · ${textoTiempoRestante(estado.pendienteDesde, ahora) ?? ''}`;
    return (
      <span className="inline-flex flex-wrap items-center gap-1">
        <span title={`Vence el ${vencimientoEnChile(estado.pendienteDesde)}`}
          className={`text-xs px-2 py-1 rounded-full font-semibold ${vencido ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-800'}`}>
          {prefijo}{texto}
        </span>
        {estado.objecionMotivo && <span className="text-xs text-red-700">{indicacionDeObjecion(estado.objecionMotivo)}</span>}
        {adjuntar}
      </span>
    );
  }

  if (estado.tipo === 'exento') {
    return <span className="text-xs text-slate-400">{prefijo}{TEXTO_EXENTO[estado.motivo]}</span>;
  }

  return (
    <span className="inline-flex items-center gap-1">
      <span className="text-xs text-slate-400">{prefijo}Sin comprobante en marcha blanca</span>
      {adjuntar}
    </span>
  );
};
